"use client";

import * as React from "react";

import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "@repo/ui/lib/utils";

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-2", className)}
      {...props}
    />
  );
}

function TabsList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "bg-muted text-muted-foreground inline-flex h-9 w-fit items-center justify-center rounded-lg p-[3px]",
        className,
      )}
      {...props}
    />
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "data-[state=active]:bg-background dark:data-[state=active]:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:outline-ring dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 text-foreground dark:text-muted-foreground inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:outline-1 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:shadow-sm [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export interface TabItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  triggerClassName?: string;
  contentClassName?: string;
  shouldRender?: boolean;
  badge?: string | number;
  badgeClassName?: string;
  disabled?: boolean;
}

interface CustomTabsProps {
  items: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  listClassName?: string;
  triggerClassName?: string;
  contentClassName?: string;
  contentWrapperClassName?: string;
  orientation?: "horizontal" | "vertical";
  renderTrigger?: (item: TabItem, isActive: boolean) => React.ReactNode;
  renderContent?: (item: TabItem, isActive: boolean) => React.ReactNode;
}

export function CustomTabs({
  items,
  defaultValue,
  value: controlledValue,
  onValueChange,
  className = "",
  listClassName = "",
  triggerClassName = "",
  contentClassName = "",
  contentWrapperClassName = "",
  orientation = "horizontal",
  renderTrigger,
  renderContent,
}: Readonly<CustomTabsProps>) {
  const [internalValue, setInternalValue] = React.useState(
    controlledValue ?? defaultValue ?? items.find((i) => i.shouldRender !== false)?.value ?? "",
  );

  const currentValue = controlledValue ?? internalValue;

  const handleValueChange = (value: string) => {
    if (!controlledValue) {
      setInternalValue(value);
    }
    onValueChange?.(value);
  };

  // Filter items based on shouldRender
  const visibleItems = items.filter((item) => item.shouldRender !== false);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <Tabs
      value={currentValue}
      onValueChange={handleValueChange}
      orientation={orientation}
      className={className}
    >
      <TabsList className={listClassName}>
        {visibleItems.map((item) => {
          const isActive = currentValue === item.value;
          return (
            <TabsTrigger
              key={item.value}
              value={item.value}
              disabled={item.disabled}
              className={cn(triggerClassName, item.triggerClassName)}
            >
              {renderTrigger ? (
                renderTrigger(item, isActive)
              ) : (
                <div className="flex items-center gap-2">
                  {item.icon && <span className="flex-shrink-0">{item.icon}</span>}
                  <span>{item.label}</span>
                  {item.badge && (
                    <span
                      className={cn(
                        "ml-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800",
                        item.badgeClassName,
                      )}
                    >
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <div className={contentWrapperClassName}>
        {visibleItems.map((item) => {
          const isActive = currentValue === item.value;
          return (
            <TabsContent
              key={item.value}
              value={item.value}
              className={cn(contentClassName, item.contentClassName)}
            >
              {renderContent ? renderContent(item, isActive) : item.content}
            </TabsContent>
          );
        })}
      </div>
    </Tabs>
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
export default CustomTabs;
